import { useEffect, useState } from 'react';
import { rolesApi } from '@api/roles';
import { usersApi, type UserDto } from '@api/users';
import { HttpError } from '@api/http';

export default function UsersAdminPage(): JSX.Element {
  const [users, setUsers] = useState<UserDto[]>([]);
  const [roles, setRoles] = useState<Array<{ id: number; name: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    email: '',
    fullName: '',
    password: '',
    roleId: '',
    isActive: true,
  });

  const load = async (): Promise<void> => {
    const [nextUsers, nextRoles] = await Promise.all([usersApi.list(), rolesApi.list()]);
    setUsers(nextUsers);
    setRoles(nextRoles.map((role) => ({ id: role.id, name: role.name })));
  };

  useEffect(() => {
    load().catch((err: unknown) => {
      const message =
        err instanceof HttpError ? err.message : err instanceof Error ? err.message : 'Failed to load users';
      setError(message);
    });
  }, []);

  return (
    <div className="mx-auto flex w-full max-w-screen-xl flex-col gap-4 p-6">
      <h1 className="text-xl font-semibold">Users</h1>
      {error ? <p className="text-sm text-danger">{error}</p> : null}

      <form
        className="grid gap-2 rounded-md border border-border bg-surface p-3 md:grid-cols-5"
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          usersApi
            .create({
              email: form.email,
              fullName: form.fullName,
              password: form.password,
              roleId: Number(form.roleId),
              isActive: form.isActive,
            })
            .then(() => {
              setForm({ email: '', fullName: '', password: '', roleId: '', isActive: true });
              return load();
            })
            .catch((err: unknown) => {
              const message =
                err instanceof HttpError
                  ? err.message
                  : err instanceof Error
                    ? err.message
                    : 'Failed to create user';
              setError(message);
            })
            .finally(() => setBusy(false));
        }}
      >
        <input
          className="rounded border border-border bg-bg px-2 py-1.5 text-sm"
          placeholder="Full name"
          value={form.fullName}
          onChange={(e) => setForm((s) => ({ ...s, fullName: e.target.value }))}
          required
        />
        <input
          className="rounded border border-border bg-bg px-2 py-1.5 text-sm"
          placeholder="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
          required
        />
        <input
          className="rounded border border-border bg-bg px-2 py-1.5 text-sm"
          placeholder="Password"
          type="password"
          value={form.password}
          onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))}
          required
          minLength={8}
        />
        <select
          className="rounded border border-border bg-bg px-2 py-1.5 text-sm"
          value={form.roleId}
          onChange={(e) => setForm((s) => ({ ...s, roleId: e.target.value }))}
          required
        >
          <option value="">Select role</option>
          {roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </select>
        <button
          type="submit"
          disabled={busy}
          className="rounded bg-accent px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
        >
          {busy ? 'Creating...' : 'Create user'}
        </button>
      </form>

      <div className="overflow-auto rounded-md border border-border bg-surface">
        <table className="w-full min-w-[680px] text-sm">
          <thead className="bg-bg/40 text-left text-muted">
            <tr>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="border-t border-border">
                <td className="px-3 py-2">{user.fullName}</td>
                <td className="px-3 py-2">{user.email}</td>
                <td className="px-3 py-2">{user.role?.name ?? '-'}</td>
                <td className="px-3 py-2">{user.isActive ? 'Active' : 'Inactive'}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    className="rounded border border-border px-2 py-1 text-xs hover:border-danger hover:text-danger"
                    onClick={() => {
                      usersApi
                        .remove(user.id)
                        .then(() => load())
                        .catch((err: unknown) => {
                          const message =
                            err instanceof HttpError
                              ? err.message
                              : err instanceof Error
                                ? err.message
                                : 'Failed to delete user';
                          setError(message);
                        });
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
